import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Product, CartItem, Order, Address } from '../types';
import { api } from '../constants';

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product) => boolean;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  checkout: (userId: string, businessName: string, address: Address, paymentMethod: string, items?: CartItem[]) => Promise<Order | null>;
  orders: Order[];
  loadOrders: (userId: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('piggy_dental_cart');
    if (savedCart) setCart(JSON.parse(savedCart));
  }, []);

  useEffect(() => {
    localStorage.setItem('piggy_dental_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: Product): boolean => {
    let success = true;
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
            success = false;
            return prev; 
        }
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      if (product.stock > 0) {
          return [...prev, { ...product, quantity: 1 }];
      }
      success = false;
      return prev;
    });
    return success;
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item => {
      if (item.id === productId) {
          // Check stock limit if increasing quantity
          if (quantity > item.quantity && quantity > item.stock) {
              return item;
          }
          return { ...item, quantity };
      }
      return item;
    }));
  };

  const clearCart = useCallback(() => setCart([]), []);

  const loadOrders = useCallback(async (userId: string) => {
    try {
      const fetchedOrders = await api.getOrders(userId);
      setOrders(fetchedOrders);
    } catch (error) {
      console.warn('Failed to load orders, using local storage');
      const savedOrders = localStorage.getItem('piggy_dental_orders');
      if (savedOrders) setOrders(JSON.parse(savedOrders));
    }
  }, []);

  const checkout = useCallback(async (userId: string, businessName: string, address: Address, paymentMethod: string, items?: CartItem[]): Promise<Order | null> => {
    const itemsToCheckout = items || cart;
    if (itemsToCheckout.length === 0) return null;
    
    const subtotal = itemsToCheckout.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = subtotal * 0.12;
    const total = subtotal + tax;
    
    const orderData = {
      userId,
      businessName,
      items: itemsToCheckout,
      subtotal,
      tax,
      total,
      shippingAddress: address,
      paymentMethod
    };

    try {
      const newOrder = await api.createOrder(orderData);
      setOrders(prev => [newOrder, ...prev]);
      
      // Only remove checked-out items from cart
      if (items) {
          const itemIds = items.map(i => i.id);
          setCart(prev => prev.filter(i => !itemIds.includes(i.id)));
      } else {
          setCart([]);
      }
      
      return newOrder;
    } catch (error) {
      // Fallback to local storage
      const newOrder: Order = {
        id: `ORD-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        ...orderData,
        date: new Date().toISOString(),
        status: 'ORDERED'
      };
      
      setOrders(prev => {
        const updated = [newOrder, ...prev];
        localStorage.setItem('piggy_dental_orders', JSON.stringify(updated));
        return updated;
      });

      if (items) {
          const itemIds = items.map(i => i.id);
          setCart(prev => prev.filter(i => !itemIds.includes(i.id)));
      } else {
          setCart([]);
      }

      return newOrder;
    }
  }, [cart]);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, checkout, orders, loadOrders }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
